import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminAPI } from '../services/api';

const ACCOUNT_STATUS_BADGE = {
  complete: 'bg-green-100 text-green-700',
  incomplete: 'bg-amber-100 text-amber-800',
  suspended: 'bg-orange-100 text-orange-800',
  banned: 'bg-red-100 text-red-700',
  deleted: 'bg-gray-200 text-gray-700',
  unknown: 'bg-gray-100 text-gray-700',
};

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

function fmtValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-sm text-gray-900 break-words ${mono ? 'font-mono text-xs' : ''}`}>
        {typeof value === 'object' && value !== null && !Array.isArray(value) ? (
          <pre className="whitespace-pre-wrap text-xs bg-gray-50 rounded p-2 overflow-x-auto">{fmtValue(value)}</pre>
        ) : (
          fmtValue(value)
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <h2 className="text-lg font-bold text-gray-900 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
      <span className="text-gray-600 font-semibold text-sm">{label}</span>
      <span className="text-gray-900 font-bold">{value ?? 0}</span>
    </div>
  );
}

function UserDetails() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUserDetails(userId);
      setUser(response.data.user);
      setMeta(response.data.meta);
      setStats(response.data.stats);
      setDeviceInfo(response.data.deviceInfo || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const updateRestriction = async (action) => {
    const labels = { ban: 'permanently ban', unban: 'unban', suspend: 'temporarily suspend', unsuspend: 'restore' };
    if (!window.confirm(`Are you sure you want to ${labels[action]} this user?`)) return;
    try {
      setActionBusy(true);
      if (action === 'ban' || action === 'unban') {
        await adminAPI.banUser(userId, { banned: action === 'ban', reasonCode: 'SAFETY' });
      } else {
        await adminAPI.updateUser(userId, { suspended: action === 'suspend', suspensionReasonCode: 'UNDER_REVIEW' });
      }
      await fetchUserDetails();
    } catch (err) {
      window.alert(err.response?.data?.error || 'Could not update account restriction.');
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (error || !user) {
    return (
      <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error || 'User not found'}
      </div>
    );
  }

  const accountStatus = meta?.accountStatus || 'unknown';
  const statusClass = ACCOUNT_STATUS_BADGE[accountStatus] || ACCOUNT_STATUS_BADGE.unknown;

  return (
    <div className="space-y-6">
      <Link to="/users" className="text-pink-500 hover:text-pink-600 font-semibold inline-block">
        ← Back to Users
      </Link>

      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {user.profileName || user.username || user.email || 'User'}
            </h1>
            <p className="text-gray-500 mt-1 font-mono text-sm">{user._id}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusClass}`}>
              {accountStatus}
            </span>
            {user.isBanned && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">Banned (permanent)</span>
            )}
            {user.suspended && !user.isBanned && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">Suspended (temporary)</span>
            )}
            {user.isDeleted && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-700">Deleted</span>
            )}
            {user.isOnline && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Online</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <Section title="Account & signup">
              <DetailRow label="Email" value={user.email} />
              <DetailRow label="Username" value={user.username ? `@${user.username}` : null} />
              <DetailRow label="Profile name" value={user.profileName} />
              <DetailRow label="Account status" value={accountStatus} />
              <DetailRow label="Profile setup complete" value={meta?.profileSetup ?? user.profileSetup} />
              <DetailRow label="Signup step" value={meta?.profileSetupStepLabel} />
              <DetailRow label="Signup step (raw)" value={meta?.profileSetupStep ?? user.profileSetupStep} />
              <DetailRow label="Email verified (isVerified)" value={user.isVerified} />
              <DetailRow label="Created" value={fmtDate(user.createdAt)} />
              <DetailRow label="Last seen" value={fmtDate(user.lastSeen)} />
              {user.deletedAt && <DetailRow label="Deleted at" value={fmtDate(user.deletedAt)} />}
              {user.suspendedAt && <DetailRow label="Suspended at" value={fmtDate(user.suspendedAt)} />}
              {user.bannedAt && <DetailRow label="Banned at" value={fmtDate(user.bannedAt)} />}
              {user.suspensionReasonCode && (
                <DetailRow label="Restriction reason (client)" value={user.suspensionReasonCode} />
              )}
            </Section>

            <Section title="Profile">
              <DetailRow label="About" value={user.about} />
              <DetailRow label="Date of birth" value={fmtDate(user.DOB)} />
              <DetailRow label="Gender" value={user.gender} />
              <DetailRow label="Sexuality" value={user.sexuality} />
              <DetailRow label="Ethnicity" value={user.ethnicity} />
              <DetailRow label="Height" value={user.height} />
              <DetailRow label="Weight" value={user.weight} />
              <DetailRow label="Interests" value={user.interests} />
              <DetailRow label="Preferences" value={user.preferences} />
              <DetailRow label="Limits" value={user.limits} />
              <DetailRow label="HIV status" value={user.hivStatus} />
              <DetailRow label="Last tested HIV" value={fmtDate(user.lastTestedHivDate)} />
              <DetailRow label="Additional sexual health info" value={user.additionalSexualHealthInfo} />
              <DetailRow label="Leader points" value={user.leaderPoints} />
              <DetailRow label="Profile audio visibility" value={user.profileAudioVisibility} />
              <DetailRow label="Profile audio" value={user.profileAudio} />
            </Section>

            <Section title="Verification">
              <DetailRow label="Verification state" value={user.verificationState} />
              <DetailRow label="Verification status" value={user.verificationStatus} />
              <DetailRow label="Profile verified" value={user.isProfileVerified} />
              <DetailRow label="Photo verified" value={user.isPhotoVerified} />
              <DetailRow label="Verification badge shown" value={user.verificationBadge} />
              <DetailRow label="Badge type" value={user.verificationBadgeType} />
              <DetailRow label="Verification mode" value={user.verificationMode} />
              <DetailRow label="Verification visibility" value={user.verificationVisibility} />
              <DetailRow label="Show public badge" value={user.showPublicBadge} />
              <DetailRow label="Verified at" value={fmtDate(user.verifiedAt)} />
              <DetailRow label="Submitted at" value={fmtDate(user.verificationSubmittedAt)} />
              <DetailRow label="Retry reason" value={user.retryReason} />
              <DetailRow label="Fraud flagged" value={user.verificationFraudFlagged} />
              <DetailRow label="Legal hold" value={user.verificationLegalHold} />
              {Array.isArray(user.photoVerifications) && user.photoVerifications.length > 0 && (
                <DetailRow label="Photo verifications" value={user.photoVerifications} />
              )}
              {user.verificationData && (
                <DetailRow label="Verification data (sanitized)" value={user.verificationData} />
              )}
            </Section>

            {Array.isArray(user.profilePictures) && user.profilePictures.length > 0 && (
              <Section title="Profile pictures">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {user.profilePictures.map((url, index) => (
                    <a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer" className="block">
                      <img
                        src={url}
                        alt={`Profile ${index + 1}`}
                        className="w-full aspect-square object-cover rounded-lg border border-gray-200"
                      />
                    </a>
                  ))}
                </div>
                {Array.isArray(user.profilePictureValidations) && user.profilePictureValidations.length > 0 && (
                  <div className="mt-4">
                    <DetailRow label="Picture validations" value={user.profilePictureValidations} />
                  </div>
                )}
              </Section>
            )}
          </div>

          <div className="space-y-6">
            <Section title="Safety actions">
              <div className="grid grid-cols-2 gap-2">
                {user.isBanned ? (
                  <button disabled={actionBusy} onClick={() => updateRestriction('unban')} className="px-3 py-2 rounded-lg bg-gray-800 text-white font-semibold">Unban</button>
                ) : (
                  <button disabled={actionBusy} onClick={() => updateRestriction('ban')} className="px-3 py-2 rounded-lg bg-red-600 text-white font-semibold">Permanent ban</button>
                )}
                {!user.isBanned && (user.suspended ? (
                  <button disabled={actionBusy} onClick={() => updateRestriction('unsuspend')} className="px-3 py-2 rounded-lg bg-green-600 text-white font-semibold">End suspension</button>
                ) : (
                  <button disabled={actionBusy} onClick={() => updateRestriction('suspend')} className="px-3 py-2 rounded-lg bg-orange-500 text-white font-semibold">Suspend</button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">Suspension is temporary. A permanent ban also removes the member from active use.</p>
            </Section>

            <Section title="Activity">
              <div className="space-y-2">
                <StatCard label="Conversations" value={stats?.conversations} />
                <StatCard label="Messages sent" value={stats?.messagesSent} />
                <StatCard label="Messages received" value={stats?.messagesReceived} />
                <StatCard label="Messages (total)" value={stats?.messages} />
                <StatCard label="Albums" value={stats?.albums} />
                <StatCard label="Pings" value={stats?.pings} />
                <StatCard label="Pending requests sent" value={stats?.pendingRequestsSent} />
                <StatCard label="Pending requests received" value={stats?.pendingRequestsReceived} />
                <StatCard label="Safe Meets planned" value={stats?.safeMeetsInitiated} />
                <StatCard label="Safe Meet invitations" value={stats?.safeMeetsAsParticipant} />
                <StatCard label="Safe Meet help requests" value={stats?.safeMeetHelpRequests} />
                <StatCard label="Favorites" value={stats?.favorites} />
                <StatCard label="Blocked users" value={stats?.blockedUsers} />
              </div>
            </Section>

            <Section title="Privacy & messaging">
              <DetailRow label="Incognito active" value={Boolean(user.incognitoEnabled)} />
              <DetailRow label="Message Requests enabled" value={Boolean(user.messageRequestsEnabled)} />
              <p className="mt-3 text-xs text-gray-500">
                These are member-controlled settings. Admin displays them for support diagnostics and does not override them.
              </p>
            </Section>

            <Section title="App & device">
              <DetailRow label="Platform" value={user.clientApp?.platform || user.deviceType} />
              <DetailRow label="App version" value={user.clientApp?.version} />
              <DetailRow label="App build" value={user.clientApp?.build} />
              <DetailRow label="Client last seen" value={fmtDate(user.clientApp?.lastSeenAt)} />
              <DetailRow label="Social sign-in" value={user.social} />
              {Array.isArray(user.fcmDevices) && user.fcmDevices.length > 0 && (
                <DetailRow label="Push devices" value={user.fcmDevices} />
              )}
            </Section>

            <Section title="IP & Device IDs">
              <DetailRow label="Registration IP" value={deviceInfo?.registrationIp} />
              <DetailRow label="Last known IP" value={deviceInfo?.lastIp || user.lastIp} />
              {deviceInfo?.deviceIds?.length > 0 && (
                <DetailRow label="Device IDs" value={deviceInfo.deviceIds.join(', ')} />
              )}
              {deviceInfo?.fcmDevices?.length > 0 && (
                <div className="mt-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Registered Devices</span>
                  <div className="mt-1 space-y-1">
                    {deviceInfo.fcmDevices.map((d, i) => (
                      <div key={i} className="text-xs bg-gray-50 rounded px-2 py-1">
                        <span className="font-medium">{d.platform}</span>
                        {d.appVersion && <span className="ml-2 text-gray-500">v{d.appVersion}</span>}
                        {d.deviceId && <span className="ml-2 text-gray-400 font-mono">{d.deviceId.substring(0, 16)}...</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {deviceInfo?.ipHistory?.length > 0 && (
                <div className="mt-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase">IP History (last 20)</span>
                  <div className="mt-1 max-h-32 overflow-y-auto space-y-0.5">
                    {deviceInfo.ipHistory.slice().reverse().map((entry, i) => (
                      <div key={i} className="text-xs text-gray-600 flex justify-between">
                        <span className="font-mono">{entry.ip}</span>
                        <span className="text-gray-400">{entry.at ? new Date(entry.at).toLocaleDateString() : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            <Section title="Location">
              <DetailRow
                label="Coordinates"
                value={
                  user.location?.coordinates
                    ? `${user.location.coordinates[1]}, ${user.location.coordinates[0]}`
                    : null
                }
              />
              <DetailRow label="City" value={user.locationMeta?.city} />
              <DetailRow label="State" value={user.locationMeta?.state} />
              <DetailRow label="Country" value={user.locationMeta?.country} />
              <DetailRow label="Location tagged at" value={fmtDate(user.locationMeta?.taggedAt)} />
            </Section>

            <Section title="Subscription">
              <DetailRow label="Active" value={user.subscription?.isActive} />
              <DetailRow label="Plan" value={user.subscription?.plan || user.subscription?.planId} />
              <DetailRow label="Platform" value={user.subscription?.platform} />
              <DetailRow label="Start" value={fmtDate(user.subscription?.startDate)} />
              <DetailRow label="End" value={fmtDate(user.subscription?.endDate)} />
              <DetailRow label="Auto renew" value={user.subscription?.autoRenew} />
              <DetailRow label="Cancelled at" value={fmtDate(user.subscription?.cancelledAt)} />
            </Section>
          </div>
        </div>

        <div className="mt-8">
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="text-sm font-semibold text-pink-600 hover:text-pink-700"
          >
            {showRaw ? 'Hide' : 'Show'} full sanitized user document
          </button>
          {showRaw && (
            <pre className="mt-3 p-4 bg-gray-900 text-gray-100 text-xs rounded-xl overflow-x-auto max-h-[480px]">
              {JSON.stringify(user, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserDetails;
